import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Box,
  Button,
  Card,
  CardActions,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { UploadableMap } from "relay-runtime";
import { InternBrandStyleLogo_brand$key } from "./__generated__/InternBrandStyleLogo_brand.graphql";
import useBrandAppConfigUpdate2Mutation from "../../../../mutations/useBrandAppConfigUpdate2Mutation";
import InternBrandStyleLogoItem, {
  BrandLogo,
} from "./InternBrandStyleLogoItem";
import InternLogo from "./InternLogo";

const brandFragment = graphql`
  fragment InternBrandStyleLogo_brand on BrandType {
    id
    internStyle {
      logos {
        type
        src
        html
        width
        height
      }
    }
    appConfig {
      id
      buildTheme {
        id
        logoUrl
      }
    }
  }
`;

type InternBrandStyleLogoProps = {
  brand: InternBrandStyleLogo_brand$key;
  onLogoSelect?: (logo: BrandLogo) => void;
};

const InternBrandStyleLogo = ({
  brand: brandKey,
  onLogoSelect,
}: InternBrandStyleLogoProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [updateBrandAppConfig, isMutationInFlight] =
    useBrandAppConfigUpdate2Mutation();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedLogo, setSelectedLogo] = useState<BrandLogo | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<string>("#FFFFFF");

  const logos = useMemo(() => {
    if (!brand.internStyle?.logos) return [];
    return brand.internStyle.logos;
  }, [brand.internStyle?.logos]);

  const savedLogoUrl: string | null = useMemo(() => {
    return brand.appConfig?.buildTheme?.logoUrl ?? null;
  }, [brand.appConfig?.buildTheme?.logoUrl]);

  useEffect(() => {
    if (selectedIndex === null || logos.length <= selectedIndex) {
      return;
    }
    setSelectedLogo(logos[selectedIndex]);
  }, [selectedIndex, logos]);

  const hasChanges = !!selectedLogo;

  const handleLogoSelect = (logo: BrandLogo, index: number) => {
    setSelectedIndex(index);
    onLogoSelect?.(logo);
  };

  const handleSave = () => {
    if (!selectedLogo) return;

    const buildThemePatch = selectedLogo.src
      ? {
          logoUrl: selectedLogo.src,
        }
      : {};

    const uploadables: UploadableMap = {};
    if (!selectedLogo.src) {
      const svgBlob = new Blob([selectedLogo.html], {
        type: "image/svg+xml;charset=utf-8",
      });
      uploadables["buildTheme.logo"] = svgBlob;
    }

    updateBrandAppConfig(
      {
        id: brand.appConfig?.id,
        buildTheme: {
          id: brand.appConfig?.buildTheme?.id,
          ...buildThemePatch,
        },
      },
      () => {},
      uploadables
    );
  };

  return (
    <Card
      sx={{
        p: 2,
        width: "100%",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Logo
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={backgroundColor !== "#FFFFFF"}
              onChange={(e) =>
                setBackgroundColor(e.target.checked ? "#000000" : "#FFFFFF")
              }
              size="small"
            />
          }
          label="Preview background color"
        />
      </Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={4}
        sx={{ width: "100%", minWidth: 0, flex: 1 }}
      >
        {/* Left side - Logo list */}
        <Box
          sx={{
            flex: { sm: "0 0 270px" },
            width: { xs: "100%", sm: 270 },
            maxWidth: "100%",
            position: "relative",
            minHeight: "180px",
          }}
        >
          <Box
            p={1}
            sx={{
              overflowY: "auto",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            {logos?.map((logo, index) => (
              <InternBrandStyleLogoItem
                key={index}
                index={index}
                logo={logo}
                selectable={true}
                isSelected={index === selectedIndex}
                onClick={() => handleLogoSelect(logo, index)}
                size="small"
                logoBackgroundColor={backgroundColor}
              />
            ))}
          </Box>
        </Box>

        {/* Right side - Selected logo preview */}
        <Stack
          sx={{ flex: "1 1 auto", minWidth: 0 }}
          alignItems="start"
          direction="row"
          spacing={2}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <Box sx={{ backgroundColor }}>
              <InternLogo logo={savedLogoUrl ?? undefined} size="large" />
            </Box>

            {selectedLogo && (
              <>
                <ArrowDownwardIcon fontSize="small" />
                <Box sx={{ backgroundColor }}>
                  <InternLogo logo={selectedLogo} size="large" />
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          mt: "auto",
          borderTop: "1px solid #DBDCDD",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={!hasChanges || isMutationInFlight}
          onClick={handleSave}
        >
          {isMutationInFlight ? "Saving..." : "Save"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InternBrandStyleLogo;
